import FormControl from '@material-ui/core/FormControl'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { sortBy, find } from 'lodash'
import PropTypes from 'prop-types'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { ORGANIZATION_ID_URL_QUERY_PARAM } from '../../../Constants'
import DisplayError from '../../../fhg/components/DisplayError'
import FHGTypography from '../../../fhg/components/Typography'
import { isPlatformAdmin } from '../../../Utils/ServerUtil'
import { getUrlParameter } from '../../../Utils/helpers'
import { useFetchOrganizations } from '../../../hooks/useFetchOrganizations'

const useStyles = makeStyles(
    (theme) => ({
        formControl: {
            color: 'inherit',
            backgroundColor: 'transparent',
            '&:focus': {
                backgroundColor: 'transparent',
            },
        },
        titleStyle: {
            fontSize: '14px !important',
            fontWeight: 500,
            lineHeight: '17px',
            textAlign: 'center',
            color: theme.palette.secondary.contrastText,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('xs')]: {
                whiteSpace: 'wrap',
            },
        },
        selectStyle: {
            color: theme.palette.environment.light.level3.base, //'#E6E3DC',
            '& svg': {
                fill: theme.palette.environment.light.level3.base, //'#E6E3DC'
            },
        },
        menuText: {
            display: 'inline',
            whiteSpace: 'normal',
        },
    }),
    'OrganizationStyles'
)

// Utility functions
const getStoredOrganizationId = () => {
    const stored = localStorage['hw.' + window.btoa('organization')]
    return stored ? window.atob(stored) : null
}

const setStoredOrganizationId = (organizationId) => {
    if (organizationId) {
        localStorage['hw.' + window.btoa('organization')] = window.btoa(organizationId)
    } else {
        localStorage.removeItem('hw.' + window.btoa('organization'))
    }
}

const updateIntercom = (organization) => {
    window.Intercom('shutdown')
    const APPID = 'fl2igt9h'
    localStorage.setItem('selectedOrg', JSON.stringify(organization))
    localStorage.setItem('intercomID', APPID)
    
    setTimeout(() => {
        window.Intercom('boot', { app_id: APPID })
    }, 1000)
}

const processOrganizations = (userOrganizations, fetchedOrganizations) => {
    // Use userOrganizations if they exist and have content
    // if (userOrganizations && userOrganizations.length > 0) {
    //     return userOrganizations
    // }
    
    // Fall back to fetched organizations
    if (!fetchedOrganizations?.length) {
        return []
    }
    
    // Find default "Listing Ops" organization
    const listingOpsOrg = fetchedOrganizations.find(org => 
        org.name.includes('Listing Ops')
    )
    
    const sortedOrgs = sortBy(fetchedOrganizations, ['created_at'])
    // Put Listing Ops first if it exists
    const ordered = listingOpsOrg 
        ? [listingOpsOrg, ...sortedOrgs.filter(org => org.organization_id !== listingOpsOrg.organization_id)]
        : sortedOrgs
    // Always include "All Organizations" first
    return [{ organization_id: '', name: 'All Organizations' }, ...ordered]
}

/**
 * Select component to select an organization to show data from that organization.
 *
 * @param onChange The callback when the organization changes.
 * @param userOrganizations list of user organization memberships (Optional)
 */
export default function OrganizationSelect({ onChange, userOrganizations }) {
    const classes = useStyles()
    const [selectedOrganization, setSelectedOrganization] = useState(() => {
        const stored = localStorage.getItem('selectedOrg')
        return stored ? JSON.parse(stored) : null
    })
    
    // Use React Query hook to fetch organizations
    const { 
        data: organizations = [], 
        error 
    } = useFetchOrganizations()
    
    // Get URL parameters
    const userOrganizationParam = getUrlParameter('userOrganization')
    const organizationIdUrlQueryParam = getUrlParameter(ORGANIZATION_ID_URL_QUERY_PARAM)
    
    // Get processed organizations list
    const processedOrganizations = useMemo(() => processOrganizations(userOrganizations, organizations), [userOrganizations, organizations])
    console.log({processOrganizations})
    
    // Main effect to handle organization selection and URL parameters
    useEffect(() => {
        if (!processedOrganizations.length) return
        
        let targetOrganization = null
        
        // Priority 1: URL parameter for platform admin
        if (organizationIdUrlQueryParam && isPlatformAdmin()) {
            targetOrganization = find(processedOrganizations, { 
                organization_id: organizationIdUrlQueryParam 
            })
            
            if (targetOrganization) {
                const url = new URL(window.location.href)
                url.searchParams.delete(ORGANIZATION_ID_URL_QUERY_PARAM)
                window.history.replaceState(null, "", url.toString())
            }
        }
        
        // Priority 2: userOrganization parameter
        if (!targetOrganization && userOrganizationParam) {
            targetOrganization = find(processedOrganizations, { 
                organization_id: userOrganizationParam 
            })
            
            const url = new URL(window.location.href)
            url.searchParams.delete('userOrganization')
            window.history.replaceState(null, "", url.toString())
        }
        
        // Priority 3: Stored organization
        if (!targetOrganization) {
            const storedId = getStoredOrganizationId()
            if (storedId) {
                targetOrganization = find(processedOrganizations, { 
                    organization_id: storedId 
                })
            }
        }
        
        // Priority 4: Default to first organization
        if (!targetOrganization) {
            targetOrganization = processedOrganizations[0]
        }
        
        // Update state and storage
        if (targetOrganization && targetOrganization.organization_id !== selectedOrganization?.organization_id) {
            setSelectedOrganization(targetOrganization)
            setStoredOrganizationId(targetOrganization.organization_id)
            updateIntercom(targetOrganization)
        }
    }, [processedOrganizations, userOrganizationParam, organizationIdUrlQueryParam, selectedOrganization])

    /**
     * Handle changes to the organization.
     * @param event The change event.
     */
    const handleChange = (event) => {
        const value = event.target.value
        if (value === '') {
            const allOrg = { organization_id: '', name: 'All Organizations' }
            setSelectedOrganization(allOrg)
            setStoredOrganizationId(null)
            updateIntercom(allOrg)
            const url = new URL(window.location.href)
            url.searchParams.delete('userOrganization')
            url.searchParams.delete(ORGANIZATION_ID_URL_QUERY_PARAM)
            window.history.replaceState(null, "", url.toString())
            onChange && onChange(event)
            return
        }
        const newOrganization = find(processedOrganizations, { organization_id: value })
        if (newOrganization) {
            setSelectedOrganization(newOrganization)
            setStoredOrganizationId(value)
            updateIntercom(newOrganization)
            const url = new URL(window.location.href)
            url.searchParams.delete('userOrganization')
            url.searchParams.delete(ORGANIZATION_ID_URL_QUERY_PARAM)
            window.history.replaceState(null, "", url.toString())
        }
        onChange && onChange(event)
    }

    return (
        <Fragment>
            {error && (
                <DisplayError
                    errorId={'evalDashboard.loadOrganizations.error'}
                    error={error?.message || 'Failed to load organizations'}
                    enableRefresh={true}
                    onClose={() => {}}
                />
            )}
            <FormControl className={classes.formControl}>
                <Select
                    value={selectedOrganization?.organization_id || ''}
                    className={classes.selectStyle}
                    displayEmpty={true}
                    renderValue={() => (
                        <Grid
                            container
                            wrap={'nowrap'}
                            direction={'row'}
                            alignItems={'center'}
                        >
                            <FHGTypography
                                variant={'button'}
                                className={classes.titleStyle}
                            >
                                {selectedOrganization?.name || 'All Organizations'}
                            </FHGTypography>
                        </Grid>
                    )}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                    }}
                    onChange={handleChange}
                    input={
                        <Input
                            name="status"
                            disableunderline="true"
                            style={{ marginRight: 8, fontSize: 18 }}
                        />
                    }
                >
                    {
                        (()=> {
                            console.log({processedOrganizations})
                        })()
                    }
                    {processedOrganizations.map((organization, index) => (
                        <MenuItem
                            key={`organization-${organization.organization_id}-${index}`}
                            value={organization.organization_id}
                        >
                            <FHGTypography
                                className={classes.menuText}
                                color="inherit"
                                variant="body1"
                            >
                                {organization.name}
                            </FHGTypography>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Fragment>
    )
}

OrganizationSelect.propTypes = {
    onChange: PropTypes.func,
}
